import { useState, type DragEvent, type FC } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import type { WorkflowColumnDef } from '../workflowColumns';

interface ColumnConfigDialogProps {
  open: boolean;
  onClose: () => void;
  /** All registry columns (used for labels); the order to render comes from `order`. */
  columns: WorkflowColumnDef[];
  /** Current column order (keys). */
  order: string[];
  visibility: Record<string, boolean>;
  onToggle: (key: string) => void;
  onMove: (key: string, direction: 'up' | 'down') => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onReset: () => void;
}

/**
 * Configure which workflow-list columns are shown and in what order. Reorder by dragging a row
 * (native HTML5 drag, mirroring ElementRankingsPage) or via the up/down arrows — the arrows are
 * required for keyboard accessibility since native drag is pointer-only.
 */
export const ColumnConfigDialog: FC<ColumnConfigDialogProps> = ({
  open,
  onClose,
  columns,
  order,
  visibility,
  onToggle,
  onMove,
  onReorder,
  onReset,
}) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const byKey = new Map<string, WorkflowColumnDef>(columns.map((c) => [c.key, c]));
  const orderedDefs = order.map((k) => byKey.get(k)).filter((c): c is WorkflowColumnDef => Boolean(c));

  const handleDragStart = (e: DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', '');
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }
    onReorder(draggedIndex, dropIndex);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => setDraggedIndex(null);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth aria-labelledby="column-config-title">
      <DialogTitle id="column-config-title" sx={{ pb: 0.5 }}>
        Configure columns
      </DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Check to show a column. Drag, or use the arrows, to reorder.
        </Typography>
        <Box role="list">
          {orderedDefs.map((col, index) => (
            <Box
              key={col.key}
              role="listitem"
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 0.5,
                py: 0.25,
                borderRadius: 1,
                cursor: 'grab',
                bgcolor: draggedIndex === index ? 'action.selected' : 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <DragIndicatorIcon fontSize="small" sx={{ color: 'text.disabled' }} aria-hidden />
              <Checkbox
                size="small"
                checked={Boolean(visibility[col.key])}
                onChange={() => onToggle(col.key)}
                slotProps={{ input: { 'aria-label': `Show ${col.label} column` } }}
              />
              <Typography variant="body2" sx={{ flex: 1 }}>
                {col.label}
              </Typography>
              <IconButton
                size="small"
                aria-label={`Move ${col.label} up`}
                disabled={index === 0}
                onClick={() => onMove(col.key, 'up')}
              >
                <KeyboardArrowUpIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                aria-label={`Move ${col.label} down`}
                disabled={index === orderedDefs.length - 1}
                onClick={() => onMove(col.key, 'down')}
              >
                <KeyboardArrowDownIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={onReset} color="inherit">
          Reset to default
        </Button>
        <Button onClick={onClose} variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ColumnConfigDialog;
